import { B400, N10, N40, N900 } from '@atlaskit/theme/colors';
import styled from '@emotion/styled';
import React, { PropsWithChildren } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDesktopMediaQuery } from '../../hooks';
import { FeatureMessage } from '../feature-message';

export type PlanCardProps = {
  first?: boolean;
  last?: boolean;
  rightHighlighted?: boolean;
  highlighted?: boolean;
  planName: string;
  ctaButton: React.ReactNode;
  featureMessagePlanName?: string;
  planFeatureMessages?: {
    message: string;
    modalTargetId?: string;
    isLabelPresent?: boolean;
  }[];
};

const PlanCard = ({
  first = false,
  last = false,
  rightHighlighted = false,
  highlighted = false,
  planName,
  ctaButton,
  featureMessagePlanName,
  planFeatureMessages,
  children
}: PropsWithChildren<PlanCardProps>) => {
  const isMobile = !useDesktopMediaQuery();

  if (isMobile) {
    first = last = true;
  }

  const baseBorderStyles = `
    border-color: ${highlighted ? B400 : N40};
    border-left-width: ${first ? '1px' : '0'};
    border-right-color: ${highlighted || rightHighlighted ? B400 : N40};
    border-right-width: 1px;
    border-top-width: 0;
    border-bottom-width: 0;
    border-style: solid;
  `;

  return (
    <>
      <Header
        first={first}
        last={last}
        highlighted={highlighted}
        baseBorderStyles={baseBorderStyles}
      >
        <PlanName highlighted={highlighted}>{planName}</PlanName>
      </Header>

      <PlanMessagingContainer
        highlighted={highlighted}
        baseBorderStyles={baseBorderStyles}
        first={first}
        last={last}
        planFeatureMessagesLength={planFeatureMessages?.length}
        isMobile={isMobile}
      >
        {children}

        <CTAButtonContainer>{ctaButton}</CTAButtonContainer>
      </PlanMessagingContainer>

      {planFeatureMessages && planFeatureMessages.length > 0 && (
        <FeatureMessagesList
          highlighted={highlighted}
          first={first}
          last={last}
          baseBorderStyles={baseBorderStyles}
          isMobile={isMobile}
        >
          {featureMessagePlanName && (
            <FeatureMessageTitle>
              <FormattedMessage
                id="featureMessageTitleText"
                defaultMessage="Everything from {featureMessagePlanName} plus:"
                description="feature message title text"
                values={{
                  featureMessagePlanName
                }}
              />
            </FeatureMessageTitle>
          )}
          {planFeatureMessages.map((featureMessage, index) => (
            <div key={index}>
              <FeatureMessage
                modalTargetId={featureMessage.modalTargetId ?? ''}
                isLabelPresent={featureMessage.isLabelPresent ?? false}
                message={featureMessage.message}
              />
            </div>
          ))}
        </FeatureMessagesList>
      )}
    </>
  );
};

const Header = styled.div<{
  first: boolean;
  last: boolean;
  highlighted: boolean;
  baseBorderStyles: string;
}>`
  ${(props) => props.baseBorderStyles}
  background-color: ${(props) => (props.highlighted ? B400 : N10)};
  border-top: 1px solid ${(props) => (props.highlighted ? B400 : N40)};
  border-top-left-radius: ${(props) => (props.first ? '9' : '0')}px;
  border-top-right-radius: ${(props) => (props.last ? '9' : '0')}px;
  color: ${(props) => (props.highlighted ? 'white' : N900)};
  font-size: 16px;
  grid-row: 1;
  line-height: 24px;
  padding: 20px 25px 20px;
  text-align: center;
`;

const PlanMessagingContainer = styled.div<{
  highlighted: boolean;
  baseBorderStyles: string;
  first: boolean;
  last: boolean;
  planFeatureMessagesLength: number | undefined;
  isMobile: boolean;
}>`
  ${(props) => props.baseBorderStyles}
  background-color: ${(props) => (props.highlighted ? '#F1F7FF' : 'white')};
  border-bottom-left-radius: ${(props) =>
    !props.planFeatureMessagesLength && props.first ? '9' : '0'}px;
  border-bottom-right-radius: ${(props) =>
    !props.planFeatureMessagesLength && props.last ? '9' : '0'}px;
  border-bottom-width: 1px;
  display: flex;
  flex-direction: column;
  grid-row: 2;
  margin-bottom: ${(props) =>
    props.isMobile && !props.planFeatureMessagesLength ? '25' : '0'}px;
  padding: 0 20px;
  text-align: center;
`;

const CTAButtonContainer = styled.div`
  margin-bottom: 32px;
`;

const PlanName = styled.h3<{
  highlighted: boolean;
}>`
  color: ${(props) => (props.highlighted ? 'white' : N900)} !important;
  font-size: 24px;
  line-height: 32px;
  margin: 0 0 8px;
  padding: 0;
`;

const FeatureMessagesList = styled.div<{
  first: boolean;
  last: boolean;
  baseBorderStyles: string;
  highlighted: boolean;
  isMobile: boolean;
}>`
  ${(props) => props.baseBorderStyles}
  background: white;
  border-bottom: 1px solid ${(props) => (props.highlighted ? B400 : N40)};
  border-bottom-left-radius: ${(props) => (props.first ? '9' : '0')}px;
  border-bottom-right-radius: ${(props) => (props.last ? '9' : '0')}px;
  font-size: 16px;
  grid-row: 3;
  line-height: 24px;
  margin-bottom: ${(props) => (props.isMobile ? '25' : '0')}px;
  padding-bottom: 20px;
  padding-right: 20px;
`;

const FeatureMessageTitle = styled.div`
  font-weight: 700;
  padding: 16px 0 0 46px;
`;

export { PlanCard };
